// App theme preference — persisted so the user's choice is honored on every launch.
//
// Preference (localStorage "theme"): "light" | "dark" | "system".
// Effective dark = pref === "dark" OR (pref === "system" AND OS prefers dark).
// Applied early via an inline script in index.html (no flash) and kept in sync here.

export type ThemePref = "light" | "dark" | "system";

const KEY = "theme";

export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* ignore */
  }
  return "system"; // default: follow the device
}

export function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function resolveDark(pref: ThemePref = getThemePref()): boolean {
  return pref === "dark" || (pref === "system" && systemPrefersDark());
}

/** Apply the resolved theme to <html> (toggles the `dark` class). */
export function applyTheme(pref: ThemePref = getThemePref()): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolveDark(pref));
}

/** Persist + apply a new preference, and notify listeners (toggles, status bar, splash). */
export function setThemePref(pref: ThemePref): void {
  try {
    localStorage.setItem(KEY, pref);
    // Explicit choice — stops the Ultimate auto-dark from overriding it.
    localStorage.setItem("theme_user_override", "true");
  } catch {
    /* ignore */
  }
  applyTheme(pref);
  try {
    window.dispatchEvent(new CustomEvent("dh-theme-change", { detail: pref }));
  } catch {
    /* ignore */
  }
}

let inited = false;
/** Apply on boot + re-apply when the OS theme changes while the pref is "system". */
export function initThemeSystem(): void {
  if (inited || typeof window === "undefined") return;
  inited = true;
  applyTheme();
  try {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemChange = () => {
      if (getThemePref() === "system") applyTheme("system");
    };
    if (typeof mq.addEventListener === "function") mq.addEventListener("change", onSystemChange);
    else if (typeof (mq as any).addListener === "function") (mq as any).addListener(onSystemChange);
  } catch {
    /* ignore */
  }
}
